import React, { useCallback, useEffect, useState } from 'react';
import { Cpu, Save } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { MenuImportModelOption, MenuImportSettings } from '../types';
import { formatDateTimeWib } from '../utils/menuImport';

const EFFORT_LABELS: Record<string, string> = {
  low: 'Rendah (low)',
  medium: 'Sedang (medium)',
  high: 'Tinggi (high)',
};

const inputClass =
  'border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100';

const formatPrice = (value?: number | null): string =>
  value === null || value === undefined || Number.isNaN(value) ? '?' : `$${value}`;

const priceHint = (option?: MenuImportModelOption | null): string =>
  option ? `${formatPrice(option.priceIn)} input / ${formatPrice(option.priceOut)} output per 1 juta token` : '';

const modelLabel = (id: string | null | undefined, models: MenuImportModelOption[]): string => {
  if (!id) return '—';
  return models.find((m) => m.id === id)?.label || id;
};

/**
 * Kartu "Pengaturan AI" di halaman Import Foto Menu: model & effort yang dipakai
 * untuk import berikutnya. Admin full bisa mengubah; readonly hanya melihat.
 */
const MenuImportAiSettingsCard: React.FC = () => {
  const { canManage } = useAuth();
  const [settings, setSettings] = useState<MenuImportSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [model, setModel] = useState('');
  const [effort, setEffort] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const apply = (data: MenuImportSettings | null) => {
    setSettings(data);
    setModel(data?.model ?? '');
    setEffort(data?.effort ?? '');
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      apply((await api.getAdminMenuImportSettings()) ?? null);
    } catch (err: any) {
      console.error('Error loading menu import settings:', err);
      setError(err?.response?.data?.message ?? 'Gagal memuat pengaturan AI.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const models = Array.isArray(settings?.options?.models) ? settings!.options!.models! : [];
  const efforts = Array.isArray(settings?.options?.efforts) ? settings!.options!.efforts! : [];
  const selectedModel = models.find((m) => m.id === model) ?? null;
  const currentModel = models.find((m) => m.id === settings?.model) ?? null;
  const dirty = !!settings && (model !== (settings.model ?? '') || effort !== (settings.effort ?? ''));
  const fromAdmin = settings?.source === 'admin';

  const handleSave = async () => {
    if (!model || !effort || !dirty) return;
    const message =
      `Ubah AI import foto menu menjadi ${modelLabel(model, models)} dengan effort ${EFFORT_LABELS[effort] ?? effort}?\n\n` +
      'Berlaku untuk semua import berikutnya di semua brand (paling lambat ±1 menit). Import yang sedang diproses tidak terpengaruh.';
    if (!window.confirm(message)) return;

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      apply((await api.updateAdminMenuImportSettings({ model, effort })) ?? null);
      setNotice('Pengaturan AI disimpan.');
    } catch (err: any) {
      console.error('Error saving menu import settings:', err);
      const errors = err?.response?.data?.errors;
      const firstError = errors ? (Object.values(errors).flat()[0] as string | undefined) : undefined;
      setError(firstError ?? err?.response?.data?.message ?? 'Gagal menyimpan pengaturan AI.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Cpu className="h-5 w-5 text-primary-600" />
          <h2 className="text-sm font-semibold text-gray-900">Pengaturan AI</h2>
          {settings ? (
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                fromAdmin ? 'bg-primary-100 text-primary-800' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {fromAdmin ? 'dari admin' : 'default server'}
            </span>
          ) : null}
        </div>
        {settings ? (
          <div className="text-xs text-gray-500">
            {fromAdmin
              ? `Terakhir diubah ${settings.updatedBy?.name ? `oleh ${settings.updatedBy.name} ` : ''}${formatDateTimeWib(settings.updatedAt)}`
              : 'Belum pernah diubah dari admin panel (memakai env server).'}
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="mt-3 text-sm text-gray-500">Memuat pengaturan...</div>
      ) : !settings ? (
        <div className="mt-3 text-sm text-red-600">
          {error ?? 'Pengaturan AI tidak tersedia.'}{' '}
          <button onClick={load} className="underline">Coba lagi</button>
        </div>
      ) : (
        <>
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Model</div>
              <div className="mt-1 text-lg font-semibold text-gray-900">{modelLabel(settings.model, models)}</div>
              <div className="text-xs text-gray-500">
                {settings.model ?? ''}{currentModel ? ` · ${priceHint(currentModel)}` : ''}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Effort</div>
              <div className="mt-1 text-lg font-semibold text-gray-900">
                {settings.effort ? EFFORT_LABELS[settings.effort] ?? settings.effort : '—'}
              </div>
              <div className="text-xs text-gray-500">Makin tinggi makin teliti, tapi lebih lambat dan lebih banyak token.</div>
            </div>
          </div>

          {canManage ? (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <div className="flex flex-wrap items-end gap-4">
                <label className="text-xs font-medium text-gray-500">
                  Ganti model
                  <select
                    value={model}
                    onChange={(e) => { setModel(e.target.value); setNotice(null); }}
                    disabled={saving}
                    className={`mt-1 block ${inputClass}`}
                  >
                    {model && !selectedModel ? <option value={model}>{model}</option> : null}
                    {models.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label || option.id} ({formatPrice(option.priceIn)} / {formatPrice(option.priceOut)})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-medium text-gray-500">
                  Effort
                  <select
                    value={effort}
                    onChange={(e) => { setEffort(e.target.value); setNotice(null); }}
                    disabled={saving}
                    className={`mt-1 block ${inputClass}`}
                  >
                    {effort && !efforts.includes(effort) ? <option value={effort}>{effort}</option> : null}
                    {efforts.map((value) => (
                      <option key={value} value={value}>{EFFORT_LABELS[value] ?? value}</option>
                    ))}
                  </select>
                </label>
                <button
                  onClick={handleSave}
                  disabled={!dirty || saving || !model || !effort}
                  className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Menyimpan...' : 'Simpan'}
                </button>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {selectedModel ? `Harga ${priceHint(selectedModel)}. ` : ''}
                Berlaku untuk import berikutnya di semua brand.
              </div>
            </div>
          ) : (
            <div className="mt-3 text-xs text-gray-500">Hanya admin full yang bisa mengubah pengaturan ini.</div>
          )}

          {error ? <div className="mt-2 text-sm text-red-600">{error}</div> : null}
          {notice ? <div className="mt-2 text-sm text-green-700">{notice}</div> : null}
        </>
      )}
    </div>
  );
};

export default MenuImportAiSettingsCard;
